// TelegramConnectModal.jsx
import React, { useEffect, useRef, useState } from "react";
import Api from "../../Api"; // <-- adjust path if needed
import Account from "../../helpers/Account"; // <-- adjust path if needed
import { telecomunication } from "../../translate";
import "../../assets/style/style.css";



export default function TelegramConnectModal({ open, onClose, onConnected }) {
  const langIndex = +localStorage.getItem('atsLang') || 1;
  const [step, setStep] = useState("phone"); // phone | code | password | done
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [accountId, setAccountId] = useState(null);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState(""); 
  // Optional status polling (if you have GET /telegram/get)
  const pollTimer = useRef(null);

  function stopPolling() {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }

  function reset() {
    stopPolling();
    setStep("phone");
    setPhone("");
    setCode("");
    setPassword("");
    setAccountId(null);
    setBusy(false);
    setErr("");
    setInfo("");
  }

  useEffect(() => {
    if (!open) reset();
    return () => stopPolling();
  }, [open]);

  // --- UPDATED API CALLS (fixed body) ---
  async function telegramStartFixed(phoneValue) {
    return Api.telegramStart(phoneValue);
  }

  async function telegramCodeFixed(codeValue) {
    return Api.telegramCode(codeValue, accountId);
  }

  async function telegramPasswordFixed(passwordValue) {
    return Api.telegramPassword(passwordValue, accountId);
  }
  // --------------------------------------

  async function onStart() {
    const ph = phone.trim();
    if (!ph) return setErr(telecomunication.modal_error_enter_phone[langIndex]);
    setBusy(true);
    setErr("");

    try {
      const r = await telegramStartFixed(ph);
      const data = r?.data || r; // depending on your Api wrapper

      if (!data?.ok) {
        setErr(data?.error || telecomunication.modal_error_start_failed[langIndex]);
        return;
      }

      setAccountId(Number(data.accountId));
      setStep("code");
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || telecomunication.modal_error_start_failed[langIndex]);
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitCode() {
    const c = code.trim();
    if (!c) return setErr(telecomunication.modal_error_enter_code[langIndex]);
    if (!accountId) return setErr(telecomunication.modal_error_missing_account[langIndex]);
    setBusy(true);
    setErr("");

    try {
      const r = await telegramCodeFixed(c);
      const data = r?.data || r;

      if (!data?.ok) {
        setInfo(telecomunication.try[langIndex] || data?.info?.[langIndex] || data?.error?.[langIndex]);
        setErr(telecomunication.modal_error_code_failed[langIndex]);
        return;
      }

      // Backend will move to waiting_password OR ready.
      // Since you didn't show a status endpoint, simplest UI behavior:
      // Ask for password only if user needs it (they'll know).
      // You can also add a "Continue" button to switch to password.
      if(data?.needPassword) {
        setStep("password");
      } else {
        setStep("done");
        onConnected?.({ accountId });
      }
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || telecomunication.modal_error_code_failed[langIndex]);
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitPassword() {
    const p = password.trim();
    if (!p) return setErr(telecomunication.modal_error_enter_password[langIndex]);
    if (!accountId) return setErr(telecomunication.modal_error_missing_account[langIndex]);
    setBusy(true);
    setErr("");

    try {
      const r = await telegramPasswordFixed(p);
      const data = r?.data || r;

      if (!data?.ok) {
        setErr(data?.error || telecomunication.modal_error_password_failed[langIndex]);
        return;
      }

      setStep("done");
      onConnected?.({ accountId });
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || telecomunication.modal_error_password_failed[langIndex]);
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div className="telegram-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="telegram-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="telegram-modal-header">
          <div>
            <div className="telegram-modal-header-title">{telecomunication.modal_title[langIndex]}</div>
            <div className="telegram-modal-header-subtitle">
              {accountId ? `accountId: ${accountId}` : telecomunication.modal_subtitle[langIndex]}
            </div>
          </div>
          <button className="telegram-modal-close-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="telegram-modal-body">
          {step === "phone" && (
            <>
              <div className="telegram-modal-hint">
                {telecomunication.modal_phone_hint[langIndex]}
              </div>

              <div className="telegram-modal-input-wrapper">
                <label className="telegram-modal-label">{telecomunication.modal_phone_label[langIndex]}</label>
                <input
                  className="telegram-modal-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={telecomunication.modal_phone_placeholder[langIndex]}
                  inputMode="tel"
                  autoFocus
                />
              </div>

              <div className="telegram-modal-row">
                <button className="telegram-modal-btn" onClick={onClose} disabled={busy}>
                  {telecomunication.modal_button_cancel[langIndex]}
                </button>
                <button className="telegram-modal-btn telegram-modal-btn-primary" onClick={onStart} disabled={busy}>
                  {busy ? telecomunication.modal_button_starting[langIndex] : telecomunication.modal_button_send_code[langIndex]}
                </button>
              </div>
            </>
          )}

          {step === "code" && (
            <>
              <div className="telegram-modal-hint">
                {telecomunication.modal_code_hint[langIndex]}
              </div>

              <div className="telegram-modal-input-wrapper">
                <label className="telegram-modal-label">{telecomunication.modal_code_label[langIndex]}</label>
                <input
                  className="telegram-modal-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={telecomunication.modal_code_placeholder[langIndex]}
                  inputMode="numeric"
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && onSubmitCode()}
                />
              </div>

              <div className="telegram-modal-row">
                <button
                  className="telegram-modal-btn"
                  onClick={() => {
                    setStep("phone");
                    setAccountId(null);
                    setCode("");
                    setPassword("");
                    setErr("");
                    setInfo("");
                  }}
                  disabled={busy}
                >
                  {telecomunication.modal_button_back[langIndex]}
                </button>
                <button className="telegram-modal-btn telegram-modal-btn-primary" onClick={onSubmitCode} disabled={busy}>
                  {busy ? telecomunication.modal_button_verifying[langIndex] : telecomunication.modal_button_verify_code[langIndex]}
                </button>
              </div>

              <div className="telegram-modal-info">
                {info ? info : ''}
              </div>
            </>
          )}

          {step === "password" && (
            <>
              <div className="telegram-modal-hint">
                {telecomunication.modal_password_hint[langIndex]}
              </div>

              <div className="telegram-modal-input-wrapper">
                <label className="telegram-modal-label">{telecomunication.modal_password_label[langIndex]}</label>
                <input
                  className="telegram-modal-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={telecomunication.modal_password_placeholder[langIndex]}
                  type="password"
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && onSubmitPassword()}
                />
              </div>

              <div className="telegram-modal-row">
                <button className="telegram-modal-btn" onClick={onClose} disabled={busy}>
                  {telecomunication.modal_button_cancel[langIndex]}
                </button>
                <button className="telegram-modal-btn telegram-modal-btn-primary" onClick={onSubmitPassword} disabled={busy}>
                  {busy ? telecomunication.modal_button_connecting[langIndex] : telecomunication.modal_button_connect[langIndex]}
                </button>
              </div>
            </>
          )}

          {step === "done" && (
            <>
              <div className="telegram-modal-hint">{telecomunication.modal_done_message[langIndex]}</div>
              <div className="telegram-modal-row">
                <button className="telegram-modal-btn telegram-modal-btn-primary" onClick={onClose}>
                  {telecomunication.modal_button_close[langIndex]}
                </button>
              </div>
            </>
          )}

          {!!err && (
            <div className="telegram-modal-error">
              {err}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
